import apiClient from './apiClient';

export type DocumentType = 
  | 'medical' 
  | 'vaccination' 
  | 'lab_result' 
  | 'xray' 
  | 'prescription' 
  | 'invoice' 
  | 'consent' 
  | 'insurance' 
  | 'passport' 
  | 'microchip' 
  | 'other';

export interface DocumentTag {
  id: string;
  name: string;
  color?: string;
  count: number;
}

export interface Document {
  id: string;
  petId: string;
  fileName: string;
  fileUrl: string;
  mimeType: string;
  size: number;
  tags: string[];
  uploadedBy: string;
  uploadedAt: string;
  ocrText?: string;
}

export interface UploadDocumentData {
  file: File;
  petId: string;
  clinicId: string;
  type: DocumentType;
  description?: string;
  tags: string[];
  enableOCR: boolean;
}

export interface UploadResponse {
  uploadUrl: string;
  fileUrl: string;
  documentId: string;
}

class DocumentsService {
  async getDocumentsForPet(petId: string): Promise<Document[]> {
    try {
      const response = await apiClient.get(`/pets/${petId}/documents`);

      return response.data.data || [];
    } catch (error) {
      console.error('Error fetching documents for pet:', error);
      throw error;
    }
  }

  async uploadDocument(data: UploadDocumentData): Promise<Document> {
    try {
      // Step 1: Get pre-signed URL
      const uploadResponse = await apiClient.post('/files', {
        mime: data.file.type,
        scope: 'pet',
        fileName: data.file.name,
        size: data.file.size
      });

      const { uploadUrl, fileUrl } = uploadResponse.data.data;

      // Step 2: Upload file to storage
      await fetch(uploadUrl, {
        method: 'PUT',
        body: data.file,
        headers: {
          'Content-Type': data.file.type
        }
      });

      // Step 3: Create document record
      const documentResponse = await apiClient.post(`/pets/${data.petId}/documents`, {
        fileUrl,
        fileName: data.file.name,
        mime: data.file.type,
        size: data.file.size,
        type: data.type,
        description: data.description,
        tags: data.tags,
        enableOCR: data.enableOCR
      });

      if (documentResponse.data.success) {
        return documentResponse.data.data;
      } else {
        throw new Error(documentResponse.data.error || 'Failed to create document record');
      }
    } catch (error) {
      console.error('Error uploading document:', error);
      throw error;
    }
  }

  async deleteDocument(documentId: string): Promise<void> {
    try {
      const response = await apiClient.delete(`/documents/${documentId}`);

      if (!response.data.success) {
        throw new Error(response.data.error || 'Failed to delete document');
      }
    } catch (error) {
      console.error('Error deleting document:', error);
      throw error;
    }
  }

  async updateDocumentTags(documentId: string, tags: string[]): Promise<Document> {
    try {
      const response = await apiClient.patch(`/documents/${documentId}`, {
        tags
      });

      if (response.data.success) {
        return response.data.data;
      } else {
        throw new Error(response.data.error || 'Failed to update document tags');
      }
    } catch (error) {
      console.error('Error updating document tags:', error);
      throw error;
    }
  }

  async searchDocuments(petId: string, query: string): Promise<Document[]> {
    try {
      const response = await apiClient.get(`/pets/${petId}/documents/search?q=${encodeURIComponent(query)}`);

      return response.data.data || [];
    } catch (error) {
      console.error('Error searching documents:', error);
      throw error;
    }
  }
}

export const documentsService = new DocumentsService();
